import mapValues from 'lodash/mapValues'
import noop from 'lodash/noop'
import size from 'lodash/size'
import { useQueryStates } from 'nuqs'
import React, { createContext, FC, useMemo } from 'react'
import useSWR from 'swr'

import { Looks } from '@Components/Looks'
import { BlockTypeWithGlobal, LookTile, ProductListBlockFragmentFragment } from '@Generated/graphql'
import { algoliaFetcher } from '@Globals/Algolia/Fetcher'
import { computeFacetDynamicConfig, countAppliedFilters, createQueryStatesKeys, parseValidQuery, parseValidQueryStates } from '@Globals/Algolia/Shared'
import {
  AlgoliaProductTile,
  BlockConfigAlgolia,
  FacetDynamicConfig,
  FacetStats,
  NumericFilter,
  Options,
  PageProductListBlockGuidedFilter,
  PageProductListBlockInjection,
  ProductSorting,
} from '@Globals/Algolia/Types'
import { algoliaProductToProductTile } from '@Globals/CMS/ProductHelpers'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { useLocale } from '@Globals/Locale'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

export interface ProductSearchStaticContextType {
  facetConfig: ProductListBlockFragmentFragment['facetConfig']
  hideEmptyFacets?: boolean
  initialFacetStats: FacetStats
  injections: PageProductListBlockInjection[]
  usePagination: boolean
}

export const ProductSearchStaticContext = createContext<ProductSearchStaticContextType>({
  facetConfig: [],
  hideEmptyFacets: false,
  initialFacetStats: {},
  injections: [],
  usePagination: false,
})

export interface ProductSearchContextType {
  count: number
  currentHits: number
  facetDynamicConfig: Record<string, FacetDynamicConfig>
  guidedFilters: PageProductListBlockGuidedFilter[] | null
  hasActiveFilter: boolean
  hits: Record<string, AlgoliaProductTile>
  hitsArray: AlgoliaProductTile[]
  numericFilters: Record<string, NumericFilter>
  objectIDs: string[]
  page: number | null
  pages: number
  sorting: ProductSorting
  title: {
    base: string
    canonical?: string
    disabled: boolean
    page?: string
  }

  resetFilters(): void
}

export const ProductSearchContext = createContext<ProductSearchContextType>({
  count: 0,
  currentHits: 0,
  objectIDs: [],
  facetDynamicConfig: {},
  guidedFilters: [],
  hasActiveFilter: false,
  hits: {},
  hitsArray: [],
  numericFilters: {},
  page: 1,
  pages: 0,
  sorting: ProductSorting.Default,
  title: {
    base: '',
    disabled: false,
  },

  resetFilters: noop,
})

export interface ProductSearchProviderProps {
  algoliaConfig?: BlockConfigAlgolia
  boosts?: string[]
  children?: React.ReactNode
  // the block's groups setting (graph-api folds the block's product count
  // into it); false vetoes the grouped_products_v2 distinct collapse
  enableGrouping?: boolean
  enableSearch?: boolean
  extraBlocks?: BlockTypeWithGlobal[]
  extraBlocksIDPrefix?: string
  facetConfig: ProductListBlockFragmentFragment['facetConfig']
  facets: string[]
  filters: string
  guidedFilters: PageProductListBlockGuidedFilter[] | null
  hideEmptyFacets?: boolean
  hitsPerPage: string
  injections: PageProductListBlockInjection[] | null
  looks?: LookTile[]
  title: string
}

// keep to 10 boosts max
const activeBoostIndexToHitIndex = [
  1,
  3,
  8,
  9,
  16,
  17,
  26,
  27,
  32,
  33,
]

export const ProductSearchProvider: FC<ProductSearchProviderProps> = ({
  algoliaConfig,
  boosts = [],
  children,
  enableGrouping = true,
  enableSearch = false,
  extraBlocks: extraBlocksBase = [],
  extraBlocksIDPrefix,
  facetConfig,
  facets,
  filters,
  guidedFilters,
  hideEmptyFacets = false,
  hitsPerPage,
  injections = [],
  looks = [],
  title,
}) => {
  const locale = useLocale()
  const { t } = useTranslation()
  // b and c share the thumbnails experience and groupProducts retrieval;
  // they differ only in Algolia distinct — b collapses to one tile per
  // colour group, c shows every member as its own tile
  const groupedProductsGroup = useSplitTest('grouped_products_v2')
  // the block's groups setting vetoes the whole grouping experience: small
  // or opted-out blocks neither collapse per colour group (distinct) nor
  // retrieve the sibling variants — without groupProducts on the hits, the
  // b/c tiles fall back to rendering only the product itself in the
  // variant selector row
  const grouped = (groupedProductsGroup === 'b' || groupedProductsGroup === 'c') && enableGrouping
  const distinct = groupedProductsGroup === 'b' && enableGrouping
  const sortingGroup = useSplitTest('sorting_v1')
  const usePagination = Boolean(algoliaConfig?.usePagination)

  const queryStatesKeysMap = useMemo(() => createQueryStatesKeys(facetConfig), [facetConfig])
  const [queryStates, setQueryStates] = useQueryStates(queryStatesKeysMap)

  const queryStatesCount = useMemo(() => countAppliedFilters(queryStates), [queryStates])

  const options = useMemo(() => {
    const result: Options = {
      distinct,
      facets,
      facetFilters: [],
      filters,
      grouped,
      numericFilters: {},
      hitsPerPage,
      locale,
      optionalFilters: algoliaConfig?.optionalFilters || [],
      page: null,
      pagination: usePagination,
      // key parity with generateOptions in Server/Blocks/ProductList.ts:
      // '' when search is enabled but no q, undefined when disabled
      query: enableSearch ? parseValidQuery(queryStates.q) : undefined,
      sorting: ProductSorting.Default,
      sortingGroup,
    }

    if (algoliaConfig?.useQueryParams) {
      Object.assign(result, parseValidQueryStates(queryStates, facetConfig, algoliaConfig!.initialFacetStats))
      if (!algoliaConfig!.usePagination) {
        result.page = null
      }
    }

    return result
  }, [algoliaConfig, distinct, enableSearch, facets, facetConfig, filters, grouped, hitsPerPage, locale, queryStates, sortingGroup, usePagination])

  const swr = useSWR(
    options,
    algoliaFetcher,
    {
      keepPreviousData: true,
      revalidateOnMount: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  const hitsArray = useMemo(() => {
    if (!usePagination || !swr.data?.hits || swr.data.hits.length === 0) {
      return []
    }

    if (boosts.length === 0) {
      return swr.data.hits.map(hit => algoliaProductToProductTile(hit))
    }

    const boostsSet = new Set(boosts)
    const activeBoostIDs = new Set<string>()
    const hits: AlgoliaProductTile[] = []
    const boostedHits = new Map<string, AlgoliaProductTile>()

    swr.data.hits.forEach((hit) => {
      if (boostsSet.has(hit.objectID)) {
        activeBoostIDs.add(hit.objectID)
        boostedHits.set(hit.objectID, algoliaProductToProductTile(hit, true))
        return
      }

      hits.push(algoliaProductToProductTile(hit))
    })

    let boostIndex = 0

    boosts.forEach((boostID) => {
      if (!activeBoostIDs.has(boostID)) {
        return
      }

      const targetIndex = activeBoostIndexToHitIndex[boostIndex]
      if (typeof targetIndex !== 'number') {
        return
      }

      const boostedHit = boostedHits.get(boostID)
      if (!boostedHit) {
        return
      }

      boostedHits.delete(boostID)
      boostIndex += 1

      if (hits.length <= targetIndex) {
        hits.push(boostedHit)
        return
      }

      hits.splice(targetIndex, 0, boostedHit)
    })

    return hits
  }, [boosts, swr.data?.hits, usePagination])

  const facetDynamicConfig = useMemo(
    () => computeFacetDynamicConfig(
      facetConfig,
      options.facetFilters,
      options.numericFilters,
      swr?.data?.facets,
      swr?.data?.facetsStats,
    ),
    [
      facetConfig,
      options.facetFilters,
      options.numericFilters,
      swr?.data?.facets,
      swr?.data?.facetsStats,
    ],
  )

  const titleSegments = useMemo(() => {
    const segments: ProductSearchContextType['title'] = {
      base: title,
      disabled: enableSearch,
    }

    if (!usePagination) {
      return segments
    }

    if (!algoliaConfig?.canonicals) {
      return segments
    }

    // Same rule as usePageCanonical: exactly one filter set to exactly one value
    const activeFilters = Object.keys(queryStates).filter(key => key.startsWith('filter_') && queryStates[key] !== null)
    if (activeFilters.length === 1) {
      const [filterKey] = activeFilters
      const values = queryStates[filterKey]

      if (Array.isArray(values) && values.length === 1) {
        const canonical = algoliaConfig.canonicals[`${filterKey}=${values[0]}`]
        if (canonical?.name?.defaults) {
          segments.canonical = t(canonical.name.defaults)
        }
      }
    }

    return segments
  }, [algoliaConfig?.canonicals, enableSearch, queryStates, t, title, usePagination])

  const valueStatic = useMemo(() => ({
    facetConfig,
    hideEmptyFacets,
    initialFacetStats: algoliaConfig?.initialFacetStats || {},
    injections: injections || [],
    usePagination,
  }), [facetConfig, hideEmptyFacets, algoliaConfig?.initialFacetStats, injections, usePagination])

  const value = useMemo(
    (): ProductSearchContextType => ({
      count: queryStatesCount,
      currentHits: swr.data?.totalHits || 0,
      objectIDs: swr.data?.objectIDs ?? [],
      facetDynamicConfig,
      guidedFilters,
      hasActiveFilter: size(options.facetFilters) !== 0 || size(options.numericFilters) !== 0,
      hits: usePagination ? {} : (algoliaConfig?.hits || {}),
      hitsArray,
      numericFilters: options.numericFilters,
      page: options.page,
      pages: swr.data?.pages || 0,
      sorting: options.sorting,
      title: titleSegments,

      resetFilters() {
        // the search text defines the result set like sorting does — resetting
        // clears the filters within it, not the search itself
        setQueryStates(prev => ({
          ...mapValues(prev, () => null),
          q: prev.q,
          sorting: prev.sorting,
        }))
      },
    }),
    [
      algoliaConfig?.hits,
      facetDynamicConfig,
      guidedFilters,
      hitsArray,
      options.facetFilters,
      options.numericFilters,
      options.page,
      options.sorting,
      queryStatesCount,
      setQueryStates,
      swr.data?.objectIDs,
      swr.data?.totalHits,
      swr.data?.pages,
      titleSegments,
      usePagination,
    ],
  )

  const [extraBlocks, showExtraBlocks] = useMemo(() => {
    if (!usePagination) {
      return [extraBlocksBase, !value.hasActiveFilter]
    }

    // if (usePagination && (!options.page || options.page === 1)) {
    //   showExtraBlocks = true
    // }

    return [extraBlocksBase, true]
  }, [extraBlocksBase, usePagination, value.hasActiveFilter])

  const extraBlocksComponents = useBlockComponents(extraBlocks, `${extraBlocksIDPrefix}-extra-block`)

  return (
    <ProductSearchStaticContext.Provider value={valueStatic}>
      <ProductSearchContext.Provider value={value}>
        {children}
        {Boolean(looks) && looks.length !== 0 && (
          <Looks
            looks={looks}
            settings={{
              // final v2 keys: Looks force version 2, which ignores the old
              // card* fields (this caller's portrait cards went square when
              // it still passed them)
              aspectRatio: 'portrait',
              enableSlider: true,
              rowsDesktop: 'auto',
              rowsMobile: 'auto',
              size: 'medium',
            }}
            title={t('common:GET_THE_LOOK')}
          />
        )}
        {showExtraBlocks ? extraBlocksComponents : null}
      </ProductSearchContext.Provider>
    </ProductSearchStaticContext.Provider>
  )
}
